import { useEffect } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { fullstory } from 'app/fullstory'
import { intercom } from 'app/intercom'
import { logWarn } from 'lib/logging'

// Layouts
import { AppLayout } from 'app/layouts/AppLayout'

// Public pages
import LoginPage from 'app/routes/login'
import ForgotPage from 'app/routes/forgot'
import ResetPage from 'app/routes/reset'
import ActivatePage from 'app/routes/activate'
import GoogleCallbackPage from 'app/routes/auth.google.callback'
import SchedulingPage from 'app/routes/scheduling.$id'
import PaperworkRuntimePage from 'app/routes/paperwork.$token'
import SchedulingSmsPrivacyPage from 'app/routes/scheduling.$id.sms-privacy'
import SchedulingSmsTermsPage from 'app/routes/scheduling.$id.sms-terms'
import FormPreviewRoute from 'app/routes/form-preview'
import SchedulingUnavailablePage from 'app/routes/scheduling.unavailable'

// Authenticated pages
import { FeatureRoute } from 'app/components/auth/FeatureRoute'
import { HomeRedirect } from 'app/components/auth/HomeRedirect'
import { LegacyListRoute } from 'app/components/lists/LegacyListRoute'
import ActionCenterPage from 'app/routes/__app/action-center/index'
import ActionCenterSchedulePage from 'app/routes/__app/action-center/schedule'
import ActionCenterWorkPage from 'app/routes/__app/action-center/work.$queueId'
import ListsPage from 'app/routes/__app/lists.$slug'
import TreatmentPlansPage from 'app/routes/__app/treatment-plans'
import RelatedContactsPage from 'app/routes/__app/related-contacts'
import AppointmentsPage from 'app/routes/__app/appointments'
import ContactsPage from 'app/routes/__app/contacts'
import ActivitiesPage from 'app/routes/__app/activities'
import TasksPage from 'app/routes/__app/tasks'
import InboxPage from 'app/routes/__app/inbox'
import PipelinesPage from 'app/routes/__app/pipelines'
import ProfilePage from 'app/routes/__app/profile'
import ScorecardsPage from 'app/routes/__app/scorecards'
import ImportPage from 'app/routes/__app/import'
import FlushPage from 'app/routes/__app/flush'
import ErrorTestPage from 'app/routes/__app/error-test'
import AppointmentsCalendarPage from 'app/routes/__app/appointments-calendar'

// Calls
import CallsLayout from 'app/routes/__app/calls'
import CallsIndex from 'app/routes/__app/calls/index'
import CallDetail from 'app/routes/__app/calls/$slug'

// Analytics
import AnalyticsMarketing from 'app/routes/__app/analytics/marketing'
import AnalyticsNewPatients from 'app/routes/__app/analytics/new-patients'
import AnalyticsFinancial from 'app/routes/__app/analytics/financial'
import AnalyticsProductionRedirect from 'app/routes/__app/analytics/production'
import AnalyticsConversions from 'app/routes/__app/analytics/conversions'
import AnalyticsAttribution from 'app/routes/__app/analytics/attribution'

// Companies
import CompaniesLocations from 'app/routes/__app/companies/locations'
import CompaniesWorkspaces from 'app/routes/__app/companies/workspaces'

// Agents
import AgentsLayout from 'app/routes/__app/agents'
import { AgentsGate, AgentsHome } from 'app/components/agents-hub/AgentsGate'
import AgentsFakeContacts from 'app/routes/__app/agents/fake-contacts/index'
import AgentsVoice from 'app/routes/__app/agents/voice'
import AgentsSms from 'app/routes/__app/agents/sms'
import AgentsAvailability from 'app/routes/__app/agents/availability'
import AgentsSettings from 'app/routes/__app/agents/settings'

// AI Agent
import AIAgentLayout from 'app/routes/__app/ai-agent'
import AIAgentIndex from 'app/routes/__app/ai-agent/index'
import AIAgentCallLog from 'app/routes/__app/ai-agent/call-log'
import AIAgentConfiguration from 'app/routes/__app/ai-agent/configuration'
import AIAgentSms from 'app/routes/__app/ai-agent/sms'
import AIAgentVoice from 'app/routes/__app/ai-agent/voice'
import AIAgentLocationDetail from 'app/routes/__app/ai-agent/$locationId'
import AIAgentLocationConfiguration from 'app/routes/__app/ai-agent/$locationId/configuration'
import AIAgentLocationSms from 'app/routes/__app/ai-agent/$locationId/sms'

// Workflows
import WorkflowsList from 'app/routes/__app/workflows/index'
import WorkflowsNew from 'app/routes/__app/workflows/new'
import WorkflowsEdit from 'app/routes/__app/workflows/$workflowId'

// Ops
import OpsLayout from 'app/routes/__app/ops'
import OpsIndex from 'app/routes/__app/ops/index'
import OpsCache from 'app/routes/__app/ops/cache'
import OpsPipelineReconcile from 'app/routes/__app/ops/pipelineReconcile'
import OpsSearchTool from 'app/routes/__app/ops/searchTool'
import OpsExtractionIndex from 'app/routes/__app/ops/extraction/index'
import OpsUsageTracking from 'app/routes/__app/ops/usage-tracking'
import OpsPromptsLayout from 'app/routes/__app/ops/prompts'
import OpsDatasetsLayout from 'app/routes/__app/ops/datasets'
import OpsEvalsLayout from 'app/routes/__app/ops/evals'

// Settings
import SettingsIndex from 'app/routes/__app/settings/index'
import SettingsUsersLayout from 'app/routes/__app/settings/users'
import SettingsUsersIndex from 'app/routes/__app/settings/users/index'
import SettingsUsersActive from 'app/routes/__app/settings/users/active'
import SettingsUsersInvited from 'app/routes/__app/settings/users/invited'
import SettingsUserDetail from 'app/routes/__app/settings/users/$userId'
import SettingsUserActivity from 'app/routes/__app/settings/users/$userId/activity'
import SettingsUserDetails from 'app/routes/__app/settings/users/$userId/details'
import SettingsUserIndex from 'app/routes/__app/settings/users/$userId/index'
import SettingsUserInvite from 'app/routes/__app/settings/users/$userId/invite'
import SettingsUserNotifications from 'app/routes/__app/settings/users/$userId/notifications'
import SettingsUserProfile from 'app/routes/__app/settings/users/$userId/profile'
import SettingsUserWorkspace from 'app/routes/__app/settings/users/$userId/workspace'
import SettingsRoles from 'app/routes/__app/settings/roles'
import SettingsWorkspace from 'app/routes/__app/settings/workspace'
import SettingsSendingDomains from 'app/routes/__app/settings/sending-domains'
import SettingsStaff from 'app/routes/__app/settings/staff'
import SettingsImports from 'app/routes/__app/settings/imports'
import SettingsFeatureToggles from 'app/routes/__app/settings/feature-toggles'
import SettingsStoredCredentials from 'app/routes/__app/settings/stored-credentials'
import SettingsTemplatesIndex from 'app/routes/__app/settings/templates/index'
import SettingsTemplatesNotes from 'app/routes/__app/settings/templates/notes'
import SettingsForms from 'app/routes/__app/settings/forms/library/index'
import SettingsFormEditor from 'app/routes/__app/settings/forms/library/$formTemplateId'
import SettingsPackets from 'app/routes/__app/settings/forms/packets/index'
import SettingsPacketEditor from 'app/routes/__app/settings/forms/packets/$paperworkTemplateId'
import SettingsTemplatesSms from 'app/routes/__app/settings/templates/sms'
import SettingsTemplatesTasks from 'app/routes/__app/settings/templates/tasks'
import SettingsConversionMonitoring from 'app/routes/__app/settings/conversion-monitoring'
import SettingsCarestackDashboard from 'app/routes/__app/settings/carestack-dashboard'
import SettingsReports from 'app/routes/__app/settings/reports/index'

// Settings — Location Settings
import SettingsLocationSettingsIndex from 'app/routes/__app/settings/location-settings/index'
import SettingsLocationSettingsDetail from 'app/routes/__app/settings/location-settings/$locationId'
import SettingsLocationSettingsDetailIndex from 'app/routes/__app/settings/location-settings/$locationId/index'
import SettingsLocationSettingsGeneral from 'app/routes/__app/settings/location-settings/$locationId/general'
import SettingsLocationSettingsFacebookAds from 'app/routes/__app/settings/location-settings/$locationId/facebook-ads'
import SettingsLocationSettingsPhoneNumbers from 'app/routes/__app/settings/location-settings/$locationId/phone-numbers'
import SettingsLocationSettingsEmail from 'app/routes/__app/settings/location-settings/$locationId/email'
import SettingsLocationSettingsPmsConfig from 'app/routes/__app/settings/location-settings/$locationId/pms-config'
import SettingsLocationSettingsCommLog from 'app/routes/__app/settings/location-settings/$locationId/comm-log'
import SettingsLocationSettingsProducts from 'app/routes/__app/settings/location-settings/$locationId/products'
import SettingsLocationSettingsIvr from 'app/routes/__app/settings/location-settings/$locationId/ivr'
import SettingsLocationSettingsBudgetIndex from 'app/routes/__app/settings/location-settings/$locationId/budget/index'
import SettingsLocationSettingsBudgetSlug from 'app/routes/__app/settings/location-settings/$locationId/budget/$budgetSlug'

// Settings — Integrations
import SettingsIntegrationsLayout from 'app/routes/__app/settings/integrations'
import SettingsIntegrationsIndex from 'app/routes/__app/settings/integrations/index'
import SettingsIntegrationsLocations from 'app/routes/__app/settings/integrations/locations'
import SettingsIntegrationsWorkspace from 'app/routes/__app/settings/integrations/workspace'

// Settings — Mapping
import SettingsMappingLayout from 'app/routes/__app/settings/mapping'
import SettingsMappingIndex from 'app/routes/__app/settings/mapping/index'
import SettingsMappingCdtCodes from 'app/routes/__app/settings/mapping/cdt-codes'
import SettingsMappingChannels from 'app/routes/__app/settings/mapping/channels'
import SettingsMappingForms from 'app/routes/__app/settings/mapping/forms'
import SettingsMappingMedia from 'app/routes/__app/settings/mapping/media'
import SettingsMappingPlatforms from 'app/routes/__app/settings/mapping/platforms'
import SettingsMappingReferral from 'app/routes/__app/settings/mapping/referral'
import SettingsMappingSources from 'app/routes/__app/settings/mapping/sources'

// Settings — Tags
import SettingsTagsLayout from 'app/routes/__app/settings/tags'
import SettingsTagsIndex from 'app/routes/__app/settings/tags/index'
import SettingsTagsEntity from 'app/routes/__app/settings/tags/$entity'

// Settings — Scheduling
import SettingsSchedulingLayout from 'app/routes/__app/settings/scheduling'
import SettingsSchedulingIndex from 'app/routes/__app/settings/scheduling/index'
import SettingsSchedulingLocationDetail from 'app/routes/__app/settings/scheduling/$locationId'
import SettingsSchedulingLocationIndex from 'app/routes/__app/settings/scheduling/$locationId/index'
import SettingsSchedulingLocationAppointmentTypes from 'app/routes/__app/settings/scheduling/$locationId/appointment-types'
import SettingsSchedulingLocationAvailability from 'app/routes/__app/settings/scheduling/$locationId/availability'
import SettingsSchedulingLocationSettings from 'app/routes/__app/settings/scheduling/$locationId/settings'

// Settings — Availability
import SettingsAvailabilityLayout from 'app/routes/__app/settings/availability'
import SettingsAvailabilityIndex from 'app/routes/__app/settings/availability/index'
import SettingsAvailabilityLocationDetail from 'app/routes/__app/settings/availability/$locationId'
import SettingsAvailabilityLocationIndex from 'app/routes/__app/settings/availability/$locationId/index'
import SettingsAvailabilityLocationAppointmentTypes from 'app/routes/__app/settings/availability/$locationId/appointment-types'
import SettingsAvailabilityLocationAvailability from 'app/routes/__app/settings/availability/$locationId/availability'
import SettingsAvailabilityLocationCalendar from 'app/routes/__app/settings/availability/$locationId/calendar'
import SettingsAvailabilityLocationSettings from 'app/routes/__app/settings/availability/$locationId/settings'
import SettingsAvailabilityLocationProviders from 'app/routes/__app/settings/availability/$locationId/providers'

// Settings — Online Scheduler
import SettingsOnlineSchedulerLayout from 'app/routes/__app/settings/online-scheduler'
import SettingsOnlineSchedulerIndex from 'app/routes/__app/settings/online-scheduler/index'
import SettingsOnlineSchedulerLocation from 'app/routes/__app/settings/online-scheduler/$locationId'

// Settings — PMS Schedules
import SettingsPmsSchedulesLayout from 'app/routes/__app/settings/pms-schedules'
import SettingsPmsSchedulesIndex from 'app/routes/__app/settings/pms-schedules/index'
import SettingsPmsSchedulesLocationIndex from 'app/routes/__app/settings/pms-schedules/$locationId/index'

// Settings — Call Data Extraction
import SettingsCallDataExtractionLayout from 'app/routes/__app/settings/call-data-extraction'
import SettingsCallDataExtractionIndex from 'app/routes/__app/settings/call-data-extraction/index'
import SettingsCallDataExtractionSlug from 'app/routes/__app/settings/call-data-extraction/$slug'

// Settings — Referral Types
import SettingsReferralTypesLayout from 'app/routes/__app/settings/referral-types'
import SettingsReferralTypesIndex from 'app/routes/__app/settings/referral-types/index'
import SettingsReferralTypesCdtCodes from 'app/routes/__app/settings/referral-types/cdt-codes'
import SettingsReferralTypesForms from 'app/routes/__app/settings/referral-types/forms'
import SettingsReferralTypesPmsReferral from 'app/routes/__app/settings/referral-types/pms-referral'

// Settings — AI Agent
import SettingsAIAgentLayout from 'app/routes/__app/settings/ai-agent'
import SettingsAIAgentIndex from 'app/routes/__app/settings/ai-agent/index'
import SettingsAIAgentLocationDetail from 'app/routes/__app/settings/ai-agent/$locationId'
import SettingsAIAgentLocationIndex from 'app/routes/__app/settings/ai-agent/$locationId/index'
import SettingsAIAgentLocationAppointmentTypes from 'app/routes/__app/settings/ai-agent/$locationId/appointment-types'
import SettingsAIAgentLocationAvailability from 'app/routes/__app/settings/ai-agent/$locationId/availability'
import SettingsAIAgentLocationSettings from 'app/routes/__app/settings/ai-agent/$locationId/settings'

export function App() {
  // Injects the third-party script tags. Both loaders no-op on origins they
  // don't cover (e.g. localhost). `AppLayout` boots/identifies the user once
  // the globals these create are available.
  useEffect(() => {
    try {
      fullstory()
    } catch (err) {
      logWarn(`fullstory`, `failed to load`, err)
    }
    try {
      intercom()
    } catch (err) {
      logWarn(`intercom`, `failed to load`, err)
    }
  }, [])

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route path="/reset" element={<ResetPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/scheduling/unavailable" element={<SchedulingUnavailablePage />} />
      <Route path="/scheduling/:id" element={<SchedulingPage />} />
      <Route path="/paperwork/:token" element={<PaperworkRuntimePage />} />
      <Route path="/scheduling/:id/sms-terms" element={<SchedulingSmsTermsPage />} />
      <Route path="/scheduling/:id/sms-privacy" element={<SchedulingSmsPrivacyPage />} />
      {/* Developer preview of the form engine, on fake data. Registered only in a dev server:
          a production build has no such route at all, so nobody can reach a patient-looking
          form that does nothing. A runtime feature toggle would be the wrong tool here, since
          this route sits outside AppLayout and an anonymous visitor has no toggles to load. */}
      {__DEV_SERVER__ && <Route path="/form-preview" element={<FormPreviewRoute />} />}

      {/* Authenticated routes */}
      <Route element={<AppLayout />}>
        {/* Every /action-center/* path sits behind the toggle gate: hiding the
            nav entry alone still leaves the URLs reachable from a bookmark. */}
        <Route
          path="/action-center"
          element={
            <FeatureRoute name="action-center">
              <ActionCenterPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/action-center/work/:queueId"
          element={
            <FeatureRoute name="action-center">
              <LegacyListRoute>
                <ActionCenterWorkPage />
              </LegacyListRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/action-center/schedule"
          element={
            <FeatureRoute name="action-center">
              <ActionCenterSchedulePage />
            </FeatureRoute>
          }
        />
        <Route
          path="/lists/:slug"
          element={
            <FeatureRoute name="grouped-navigation">
              <FeatureRoute name="action-center">
                <ListsPage />
              </FeatureRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/treatment-plans"
          element={
            <FeatureRoute name="grouped-navigation">
              <FeatureRoute name="action-center">
                <TreatmentPlansPage />
              </FeatureRoute>
            </FeatureRoute>
          }
        />
        <Route
          path="/related-contacts"
          element={
            <FeatureRoute name="related-contacts-prototype">
              <RelatedContactsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/appointments"
          element={
            <FeatureRoute name="grouped-navigation">
              <AppointmentsPage />
            </FeatureRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <LegacyListRoute>
              <ContactsPage />
            </LegacyListRoute>
          }
        />
        <Route path="/activities" element={<ActivitiesPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route
          path="/pipelines"
          element={
            <LegacyListRoute>
              <PipelinesPage />
            </LegacyListRoute>
          }
        />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/scorecards" element={<ScorecardsPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/flush" element={<FlushPage />} />
        <Route path="/error-test" element={<ErrorTestPage />} />
        <Route path="/appointments-calendar" element={<AppointmentsCalendarPage />} />

        {/* Calls */}
        <Route path="/calls" element={<CallsLayout />}>
          <Route index element={<CallsIndex />} />
          <Route path=":slug" element={<CallDetail />} />
        </Route>

        {/* Analytics */}
        <Route path="/analytics">
          <Route index element={<Navigate to="marketing" replace />} />
          <Route path="marketing" element={<AnalyticsMarketing />} />
          <Route path="new-patients" element={<AnalyticsNewPatients />} />
          <Route path="financial" element={<AnalyticsFinancial />} />
          <Route path="production" element={<AnalyticsProductionRedirect />} />
          <Route path="conversions" element={<AnalyticsConversions />} />
          <Route path="attribution" element={<AnalyticsAttribution />} />
          <Route path="revenue" element={<Navigate to="/analytics/financial" replace />} />
        </Route>

        {/* Companies */}
        <Route path="/companies">
          <Route index element={<Navigate to="locations" replace />} />
          <Route path="locations" element={<CompaniesLocations />} />
          <Route path="workspaces" element={<CompaniesWorkspaces />} />
        </Route>

        {/* Agents */}
        <Route path="/agents">
          <Route index element={<AgentsHome />} />
          <Route element={<AgentsLayout />}>
            <Route path="fake-contacts" element={<AgentsFakeContacts />} />
          </Route>
        </Route>
        <Route
          path="/agents/voice"
          element={
            <AgentsGate need="voice">
              <AgentsVoice />
            </AgentsGate>
          }
        />
        <Route
          path="/agents/sms"
          element={
            <AgentsGate need="sms">
              <AgentsSms />
            </AgentsGate>
          }
        />
        <Route
          path="/agents/availability"
          element={
            <AgentsGate need="voice">
              <AgentsAvailability />
            </AgentsGate>
          }
        />
        <Route
          path="/agents/settings"
          element={
            <AgentsGate need="any">
              <AgentsSettings />
            </AgentsGate>
          }
        />
        <Route path="/agents/my-agent" element={<Navigate to="/agents/settings" replace />} />

        {/* AI Agent */}
        <Route path="/ai-agent" element={<AIAgentLayout />}>
          <Route index element={<AIAgentIndex />} />
          <Route path="call-log" element={<AIAgentCallLog />} />
          <Route path="configuration" element={<AIAgentConfiguration />} />
          <Route path="sms" element={<AIAgentSms />} />
          <Route path="voice" element={<AIAgentVoice />} />
          <Route path=":locationId" element={<AIAgentLocationDetail />}>
            <Route path="configuration" element={<AIAgentLocationConfiguration />} />
            <Route path="sms" element={<AIAgentLocationSms />} />
          </Route>
        </Route>

        {/* Workflows */}
        <Route path="/workflows" element={<WorkflowsList />} />
        <Route path="/workflows/new" element={<WorkflowsNew />} />
        <Route path="/workflows/:workflowId" element={<WorkflowsEdit />} />

        {/* Ops */}
        <Route path="/ops" element={<OpsLayout />}>
          <Route index element={<OpsIndex />} />
          <Route path="cache" element={<OpsCache />} />
          <Route path="pipeline-reconcile" element={<OpsPipelineReconcile />} />
          <Route path="searchTool" element={<OpsSearchTool />} />
          <Route path="extraction" element={<OpsExtractionIndex />} />
          <Route path="usage-tracking" element={<OpsUsageTracking />} />
          <Route path="prompts" element={<OpsPromptsLayout />} />
          <Route path="datasets" element={<OpsDatasetsLayout />} />
          <Route path="evals" element={<OpsEvalsLayout />} />
        </Route>

        {/* Settings */}
        <Route path="/settings">
          <Route index element={<SettingsIndex />} />

          {/* Users */}
          <Route path="users" element={<SettingsUsersLayout />}>
            <Route index element={<SettingsUsersIndex />} />
            <Route path="active" element={<SettingsUsersActive />} />
            <Route path="invited" element={<SettingsUsersInvited />} />
            <Route path=":userId" element={<SettingsUserDetail />}>
              <Route index element={<SettingsUserIndex />} />
              <Route path="activity" element={<SettingsUserActivity />} />
              <Route path="details" element={<SettingsUserDetails />} />
              <Route path="invite" element={<SettingsUserInvite />} />
              <Route path="notifications" element={<SettingsUserNotifications />} />
              <Route path="profile" element={<SettingsUserProfile />} />
              <Route path="workspace" element={<SettingsUserWorkspace />} />
            </Route>
          </Route>

          <Route path="roles" element={<SettingsRoles />} />
          <Route path="workspace" element={<SettingsWorkspace />} />
          <Route path="sending-domains" element={<SettingsSendingDomains />} />
          <Route path="staff" element={<SettingsStaff />} />
          <Route path="imports" element={<SettingsImports />} />
          <Route path="feature-toggles" element={<SettingsFeatureToggles />} />
          <Route path="stored-credentials" element={<SettingsStoredCredentials />} />
          <Route path="templates">
            <Route index element={<SettingsTemplatesIndex />} />
            <Route path="sms" element={<SettingsTemplatesSms />} />
            <Route path="tasks" element={<SettingsTemplatesTasks />} />
            <Route path="notes" element={<SettingsTemplatesNotes />} />
          </Route>
          <Route
            path="forms"
            element={
              <FeatureRoute name="paperwork">
                <Outlet />
              </FeatureRoute>
            }
          >
            <Route index element={<Navigate to="/settings/forms/library" replace />} />
            <Route path="library" element={<SettingsForms />} />
            <Route path="library/:formTemplateId" element={<SettingsFormEditor />} />
            <Route path="packets" element={<SettingsPackets />} />
            <Route path="packets/:paperworkTemplateId" element={<SettingsPacketEditor />} />
          </Route>
          <Route path="conversion-monitoring" element={<SettingsConversionMonitoring />} />
          <Route path="carestack-dashboard" element={<SettingsCarestackDashboard />} />
          <Route path="reports" element={<SettingsReports />} />

          {/* Location Settings */}
          <Route path="location-settings">
            <Route index element={<SettingsLocationSettingsIndex />} />
            <Route path=":locationId" element={<SettingsLocationSettingsDetail />}>
              <Route index element={<SettingsLocationSettingsDetailIndex />} />
              <Route path="general" element={<SettingsLocationSettingsGeneral />} />
              <Route path="facebook-ads" element={<SettingsLocationSettingsFacebookAds />} />
              <Route path="phone-numbers" element={<SettingsLocationSettingsPhoneNumbers />} />
              <Route path="email" element={<SettingsLocationSettingsEmail />} />
              <Route path="pms-config" element={<SettingsLocationSettingsPmsConfig />} />
              <Route path="comm-log" element={<SettingsLocationSettingsCommLog />} />
              <Route path="products" element={<SettingsLocationSettingsProducts />} />
              <Route path="ivr" element={<SettingsLocationSettingsIvr />} />
              <Route path="budget" element={<SettingsLocationSettingsBudgetIndex />} />
              <Route path="budget/:budgetSlug" element={<SettingsLocationSettingsBudgetSlug />} />
            </Route>
          </Route>

          {/* Integrations */}
          <Route path="integrations" element={<SettingsIntegrationsLayout />}>
            <Route index element={<SettingsIntegrationsIndex />} />
            <Route path="locations" element={<SettingsIntegrationsLocations />} />
            <Route path="workspace" element={<SettingsIntegrationsWorkspace />} />
          </Route>

          {/* Mapping */}
          <Route path="mapping" element={<SettingsMappingLayout />}>
            <Route index element={<SettingsMappingIndex />} />
            <Route path="cdt-codes" element={<SettingsMappingCdtCodes />} />
            <Route path="channels" element={<SettingsMappingChannels />} />
            <Route path="forms" element={<SettingsMappingForms />} />
            <Route path="media" element={<SettingsMappingMedia />} />
            <Route path="platforms" element={<SettingsMappingPlatforms />} />
            <Route path="referral" element={<SettingsMappingReferral />} />
            <Route path="sources" element={<SettingsMappingSources />} />
          </Route>

          {/* Tags */}
          <Route path="tags" element={<SettingsTagsLayout />}>
            <Route index element={<SettingsTagsIndex />} />
            <Route path=":entity" element={<SettingsTagsEntity />} />
          </Route>

          {/* Scheduling */}
          <Route path="scheduling" element={<SettingsSchedulingLayout />}>
            <Route index element={<SettingsSchedulingIndex />} />
            <Route path=":locationId" element={<SettingsSchedulingLocationDetail />}>
              <Route index element={<SettingsSchedulingLocationIndex />} />
              <Route path="appointment-types" element={<SettingsSchedulingLocationAppointmentTypes />} />
              <Route path="availability" element={<SettingsSchedulingLocationAvailability />} />
              <Route path="settings" element={<SettingsSchedulingLocationSettings />} />
            </Route>
          </Route>

          {/* Availability */}
          <Route path="availability" element={<SettingsAvailabilityLayout />}>
            <Route index element={<SettingsAvailabilityIndex />} />
            <Route path=":locationId" element={<SettingsAvailabilityLocationDetail />}>
              <Route index element={<SettingsAvailabilityLocationIndex />} />
              <Route path="appointment-types" element={<SettingsAvailabilityLocationAppointmentTypes />} />
              <Route path="availability" element={<SettingsAvailabilityLocationAvailability />} />
              <Route path="calendar" element={<SettingsAvailabilityLocationCalendar />} />
              <Route path="settings" element={<SettingsAvailabilityLocationSettings />} />
              <Route path="providers" element={<SettingsAvailabilityLocationProviders />} />
            </Route>
          </Route>

          {/* Online Scheduler */}
          <Route path="online-scheduler" element={<SettingsOnlineSchedulerLayout />}>
            <Route index element={<SettingsOnlineSchedulerIndex />} />
            <Route path=":locationId" element={<SettingsOnlineSchedulerLocation />} />
          </Route>

          {/* PMS Schedules */}
          <Route path="pms-schedules" element={<SettingsPmsSchedulesLayout />}>
            <Route index element={<SettingsPmsSchedulesIndex />} />
            <Route path=":locationId" element={<SettingsPmsSchedulesLocationIndex />} />
          </Route>

          {/* Call Data Extraction */}
          <Route path="call-data-extraction" element={<SettingsCallDataExtractionLayout />}>
            <Route index element={<SettingsCallDataExtractionIndex />} />
            <Route path=":slug" element={<SettingsCallDataExtractionSlug />} />
          </Route>

          {/* Referral Types */}
          <Route path="referral-types" element={<SettingsReferralTypesLayout />}>
            <Route index element={<SettingsReferralTypesIndex />} />
            <Route path="cdt-codes" element={<SettingsReferralTypesCdtCodes />} />
            <Route path="forms" element={<SettingsReferralTypesForms />} />
            <Route path="pms-referral" element={<SettingsReferralTypesPmsReferral />} />
          </Route>

          {/* AI Agent Settings */}
          <Route path="ai-agent" element={<SettingsAIAgentLayout />}>
            <Route index element={<SettingsAIAgentIndex />} />
            <Route path=":locationId" element={<SettingsAIAgentLocationDetail />}>
              <Route index element={<SettingsAIAgentLocationIndex />} />
              <Route path="appointment-types" element={<SettingsAIAgentLocationAppointmentTypes />} />
              <Route path="availability" element={<SettingsAIAgentLocationAvailability />} />
              <Route path="settings" element={<SettingsAIAgentLocationSettings />} />
            </Route>
          </Route>
        </Route>

        {/* Default redirect: the user's pinned page, else the Action Center, else contacts */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
  )
}
